<script lang="ts" setup>
    import { computed, toRaw } from 'vue';
    import { JourneyNode } from '@journeys/Journeys.viewmodel';
    import { NodeType } from '@journeys/Enums';
    import { getSegmentForPreview } from '@journeys/utils/previewNodeSegment';
    import SegmentGroup from '@cwd/Views/Segments/SegmentEditor/SegmentTypes/SegmentGroup.vue';

    const props = defineProps<{
        node: JourneyNode;
        currentNodeStep?: any;
        // ancestor segments for this node, from computeCurrentNodeGraphData - computed once by
        // the parent (NodeConditions) and shared with NodeSegmentContactsPreview to avoid both
        // components independently re-running the same graph traversal
        ancestorSegments: any[];
    }>();

    // read-only summary of the conditions this node inherits from earlier nodes on its
    // journey path. Task (Email/Sms) nodes can't use the graph's captured link segments: the
    // engine resets its raw segment accumulator after every send, dropping the synthesized
    // "received email X within Y days" condition that only currentNodeStep (via createNewStep
    // / updateExistingStep) knows how to build. So for Task nodes, derive the inherited list
    // from currentNodeStep instead, filtering out this node's own segment. Filter by identity
    // rather than position: createNewStep appends the own segment last, but updateExistingStep
    // (used when the node is a merge point reached via 2+ branches) appends the merged
    // inherited-branches group last instead, so the own segment isn't always at a fixed offset.
    const incomingSegments = computed(() => {
        if (props.node.data.type === NodeType.Task && props.currentNodeStep) {
            const allSegments = props.currentNodeStep.data.SegmentDefinition.Segments || [];
            const ownSegments = new Set(toRaw(props.node.data.segment?.Segments) || []);
            return ownSegments.size ? allSegments.filter((s: any) => !ownSegments.has(toRaw(s))) : allSegments;
        }

        return props.ancestorSegments;
    });

    const incomingSegmentGroup = computed(() => ({
        Name: null,
        Type: 'SegmentGroup',
        Bool: 1,
        Segments: incomingSegments.value,
    }));
</script>

<template>
    <template v-if="incomingSegments.length">
        <h3 class="h4">{{ $Resources.NodeInheritedConditionsHeading }}</h3>

        <div class="m-lg-b">
            <SegmentGroup :segment="getSegmentForPreview(incomingSegmentGroup)" :segment-group="incomingSegmentGroup" :root="true" :view-mode="true" />
        </div>
    </template>
</template>

